import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { categoriesAPI } from '../services/api';

// Map backend icon field (or slug) to a Font Awesome class
const getIconClass = (category) => {
  if (category.icon) return category.icon;
  const slug = category.slug || '';
  if (slug.includes('phone') || slug.includes('tablet')) return 'fas fa-mobile-alt';
  if (slug.includes('laptop') || slug.includes('computer') || slug.includes('gaming')) return 'fas fa-laptop';
  if (slug.includes('audio') || slug.includes('video')) return 'fas fa-headphones';
  if (slug.includes('wearable') || slug.includes('watch')) return 'fas fa-clock';
  if (slug.includes('camera')) return 'fas fa-camera';
  if (slug.includes('accessory') || slug.includes('accessories')) return 'fas fa-plug';
  return 'fas fa-box';
};

const CategoriesSection = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    categoriesAPI.getAll()
      .then(res => {
        const data = res?.data || res || [];
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || categories.length === 0) return;
    // GSAP animations for category cards
    const cards = document.querySelectorAll('.category-card');

    cards.forEach(card => {
      const glow = card.querySelector('.card-glow');
      const iconBg = card.querySelector('.icon-bg');

      card.addEventListener('mouseenter', () => {
        if (glow && iconBg) {
          gsap.to(glow, {
            opacity: 1,
            scale: 1,
            duration: 0.3
          });

          gsap.to(iconBg, {
            scale: 1.2,
            rotation: 180,
            duration: 0.8,
            ease: 'power2.out'
          });

          gsap.to(card, {
            y: -10,
            duration: 0.3,
            ease: 'power2.out'
          });
        }
      });

      card.addEventListener('mouseleave', () => {
        if (glow && iconBg) {
          gsap.to(glow, {
            opacity: 0,
            scale: 0.8,
            duration: 0.3
          });

          gsap.to(iconBg, {
            scale: 1,
            rotation: 0,
            duration: 0.8,
            ease: 'power2.out'
          });

          gsap.to(card, {
            y: 0,
            duration: 0.3,
            ease: 'power2.out'
          });
        }
      });
    });

    return () => {
      cards.forEach(card => {
        card.removeEventListener('mouseenter', () => {});
        card.removeEventListener('mouseleave', () => {});
      });
    };
  }, [loading, categories]);

  const handleShopNowClick = (e) => {
    e.preventDefault();
    navigate('/shop');
  };

  return (
    <section className="categories-section" style={{ position: 'relative', zIndex: 1, opacity: 1, visibility: 'visible' }}>
      {/* Animated Green Background */}
      <div className="animated-bg-container">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="noise-overlay"></div>
      </div>

      {/* Section Content */}
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <div className="section-header" style={{ opacity: 1, visibility: 'visible', display: 'block' }} data-aos="fade-up">
          <span className="section-label">
            <i className="fas fa-sparkles"></i>
            Discover More
          </span>
          <h2 className="section-title" style={{ fontFamily: "'Syne', sans-serif", fontWeight: '700', letterSpacing: '1px' }}>
            <span className="title-line">Explore Our Top Categories for the </span>
            <span className="title-line highlight" style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic', fontWeight: '400' }}>Latest Gadgets and Electronics</span>
          </h2>
          <p className="section-subtitle" style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: '500', fontSize: '1.15rem' }}>
            Curated collections of premium tech, handpicked for the modern digital lifestyle
          </p>
        </div>

        {/* Categories Grid */}
        <div className="categories-grid" style={{ opacity: 1, visibility: 'visible', display: 'grid' }}>
          {loading ? (
            [0, 1, 2].map(i => (
              <div key={i} className="category-card" style={{ opacity: 0.5, minHeight: '280px', background: 'rgba(255,255,255,0.05)', borderRadius: '16px' }} />
            ))
          ) : categories.length === 0 ? (
            <p style={{ color: '#fff', gridColumn: '1/-1', textAlign: 'center', opacity: 0.7 }}>No categories available.</p>
          ) : (
            categories.map((category, index) => (
              <motion.div
                key={category.id || category._id || index}
                className={`category-card ${index === 1 ? 'featured' : ''}`}
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                style={{ opacity: 1, visibility: 'visible' }}
                data-aos="fade-up"
                data-aos-delay={100 + index * 100}
              >
                {index === 1 && <div className="featured-badge">Most Popular</div>}
                <div className="card-glow"></div>
                <div className="card-content">
                  <div className="icon-wrapper">
                    <div className="icon-bg"></div>
                    <i className={getIconClass(category)}></i>
                  </div>
                  <h3 className="card-title">{category.name}</h3>
                  <p className="card-description">{category.description || `Explore our ${category.name} collection`}</p>
                  <p className="card-detail">Find the best deals on {category.name} from verified sellers.</p>
                  <div className="card-stats">
                    <div className="stat">
                      <span className="stat-number">{category.product_count > 0 ? `${category.product_count}+` : '—'}</span>
                      <span className="stat-label">Products</span>
                    </div>
                    <div className="stat">
                      <span className="stat-number">{category.sort_order || index + 1}</span>
                      <span className="stat-label">Rank</span>
                    </div>
                  </div>
                  <a href="#" onClick={handleShopNowClick} className={`shop-btn ${index === 1 ? 'primary' : ''}`}>
                    <span>Shop Now</span>
                    <i className="fas fa-arrow-right"></i>
                  </a>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* CTA Section */}
        <div className="cta-section" style={{ opacity: 1, visibility: 'visible', display: 'block' }} data-aos="fade-up">
          <div className="cta-content">
            <div className="cta-left">
              <h3 className="cta-title" style={{ fontFamily: "'Syne', sans-serif", fontWeight: '700', letterSpacing: '1px' }}>
                <span className="text-gradient">Join Us for a </span>
                <span className="text-gradient" style={{ fontFamily: "'Libre Baskerville', serif", fontStyle: 'italic', fontWeight: '400' }}>Personalised Experience</span>
              </h3>
              <p className="cta-description" style={{ fontFamily: "'Urbanist', sans-serif", fontWeight: '500', fontSize: '1.15rem' }}>
                Register or log in to unlock a tailored shopping experience and exclusive offers just for you.
              </p>
              <div className="cta-features">
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Exclusive member discounts</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Early access to new products</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check-circle"></i>
                  <span>Personalized recommendations</span>
                </div>
              </div>
              <div className="cta-buttons">
                <a href="/login" className="cta-btn primary">
                  <span>Sign Up</span>
                  <i className="fas fa-user-plus"></i>
                </a>
                <a href="/login" className="cta-btn secondary">
                  <span>Log In</span>
                  <i className="fas fa-sign-in-alt"></i>
                </a>
              </div>
            </div>
            <div className="cta-right">
              <div className="phone-showcase">
                <img
                  src="data:image/svg+xml,%3Csvg width='400' height='500' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='400' height='500' fill='transparent'/%3E%3C/svg%3E"
                  alt="Phone showcase"
                  className="phone-img"
                />
                <div className="phone-glow"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoriesSection;
